import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import MobileLayout from '../../components/MobileLayout';
import { useAuth } from '../../contexts/AuthContext';
import { clearPendingInvite, savePendingInvite } from '../../features/invites/pendingInvite';

interface InvitePreview {
  storyId: string;
  storyTitle: string;
  inviterName: string | null;
  role: 'editor' | 'viewer';
  ownerPays: boolean;
  ownerCreditCap: number | null;
  email: string;
  emailMatches: boolean;
}

/** Where an emailed invite link lands: preview the story, then join it. */
const AcceptInvite: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      savePendingInvite(token);
      return;
    }
    // Signed in and on the invite: the remembered link has done its job.
    clearPendingInvite();
    let cancelled = false;
    axios
      .get(`/invites/${encodeURIComponent(token)}`)
      .then((res) => !cancelled && setPreview(res.data))
      .catch(
        (err) =>
          !cancelled &&
          setError(
            err?.response?.data?.message ||
              t('stories.invite.invalid', 'This invite link is not valid.'),
          ),
      );
    return () => {
      cancelled = true;
    };
  }, [isLoading, user, token, t]);

  const accept = async () => {
    setAccepting(true);
    try {
      const res = await axios.post(`/invites/${encodeURIComponent(token)}/accept`);
      navigate(`/stories/${res.data.storyId}`, { replace: true });
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t('stories.invite.acceptError', 'Could not accept the invite. Try again.'),
      );
    } finally {
      setAccepting(false);
    }
  };

  const card = (children: React.ReactNode) => (
    <MobileLayout showNav={!!user} showHeader={!!user}>
      <div className="flex-1 flex items-center justify-center p-6">
        <div
          className="ds-card w-full max-w-md rounded-2xl p-6 space-y-4 text-center"
          style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
        >
          <UserGroupIcon className="w-10 h-10 mx-auto text-primary-400" />
          {children}
        </div>
      </div>
    </MobileLayout>
  );

  if (isLoading) {
    return card(
      <div
        className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
        style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
      />,
    );
  }

  if (!user) {
    return card(
      <>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
          {t('stories.invite.signedOutTitle', "You've been invited to a story")}
        </h1>
        <p className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
          {t(
            'stories.invite.signedOutBody',
            'Sign in, or create a free account with the email address the invite was sent to. You will come straight back here.',
          )}
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold"
          >
            {t('stories.invite.signIn', 'Sign in')}
          </button>
          <button
            onClick={() => navigate('/signup')}
            className="w-full py-3 rounded-xl font-semibold border"
            style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
          >
            {t('stories.invite.signUp', 'Create an account')}
          </button>
        </div>
      </>,
    );
  }

  if (error) {
    return card(
      <>
        <p className="text-sm" style={{ color: 'var(--ds-text-primary)' }}>
          {error}
        </p>
        <button
          onClick={() => navigate('/stories')}
          className="w-full py-3 rounded-xl font-semibold border"
          style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
        >
          {t('stories.invite.toStories', 'Go to my stories')}
        </button>
      </>,
    );
  }

  if (!preview) {
    return card(
      <div
        className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
        style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
      />,
    );
  }

  const roleText =
    preview.role === 'editor'
      ? t('stories.invite.asEditor', 'as an editor: you can write, edit and use the AI')
      : t('stories.invite.asViewer', 'as a viewer: you can read along');

  return card(
    <>
      <h1 className="text-xl font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
        {preview.storyTitle}
      </h1>
      <p className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
        {t('stories.invite.body', '{{name}} invited you to collaborate, {{role}}.', {
          name: preview.inviterName || t('stories.members.unnamed', 'Unnamed writer'),
          role: roleText,
        })}
      </p>
      {preview.role === 'editor' && preview.ownerPays && (
        <p className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
          {preview.ownerCreditCap
            ? t(
                'stories.invite.ownerPaysCapped',
                'They cover your AI words on this story, up to {{cap}} a month.',
                { cap: preview.ownerCreditCap.toLocaleString() },
              )
            : t('stories.invite.ownerPays', 'They cover your AI words on this story.')}
        </p>
      )}
      {preview.emailMatches ? (
        <button
          onClick={accept}
          disabled={accepting}
          className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-xl font-semibold"
        >
          {t('stories.invite.accept', 'Join the story')}
        </button>
      ) : (
        <p className="text-sm" style={{ color: 'var(--ds-text-primary)' }}>
          {t(
            'stories.invite.wrongAccount',
            'This invite was sent to {{email}}. Sign in with that address (and verify it) to join.',
            { email: preview.email },
          )}
        </p>
      )}
      <button
        onClick={() => navigate('/stories')}
        className="w-full py-2 text-sm"
        style={{ color: 'var(--ds-text-secondary)' }}
      >
        {t('stories.invite.notNow', 'Not now')}
      </button>
    </>,
  );
};

export default AcceptInvite;
